import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

const REASONS = [
  { value: "spam", label: "Spam" },
  { value: "harassment", label: "Harassment" },
  { value: "off_topic", label: "Off-topic / workplace talk" },
  { value: "misinformation", label: "Misinformation" },
  { value: "other", label: "Other" },
];

export const ReportContentDialog = ({ open, onOpenChange, contentType, contentId, testIdPrefix }) => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const close = (o) => { onOpenChange(o); if (!o) { setReason(""); setNote(""); } };

  const submit = async () => {
    if (!reason) { toast.error("Pick a reason"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/reports`, { content_type: contentType, content_id: contentId, reason, note });
      toast.success(res.data.message || "Report sent");
      close(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send report");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-report-dialog`} onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <Flag className="w-4 h-4 text-[#E07A5F]" /> Report this post
          </DialogTitle>
          <DialogDescription>Moderators review every report. The author never sees who reported it.</DialogDescription>
        </DialogHeader>
        <RadioGroup value={reason} onValueChange={setReason} className="space-y-1.5 mt-1">
          {REASONS.map((r) => (
            <Label
              key={r.value}
              htmlFor={`${testIdPrefix}-reason-${r.value}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${reason === r.value ? "border-[#6B4E8A] bg-[#F0ECF7]" : "border-[#E5DFEC] hover:bg-[#FAF7F2]"}`}
            >
              <RadioGroupItem value={r.value} id={`${testIdPrefix}-reason-${r.value}`} data-testid={`${testIdPrefix}-reason-${r.value}`} />
              <span className="text-sm text-[#334155] font-normal">{r.label}</span>
            </Label>
          ))}
        </RadioGroup>
        <div>
          <Label className="text-xs text-[#64748B]">Add a note (optional)</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={3} placeholder="Anything that helps moderators understand the issue" className="mt-1.5 bg-white border-[#E5DFEC]" data-testid={`${testIdPrefix}-report-note`} />
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => close(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !reason} className="rounded-full bg-[#E07A5F] hover:bg-[#C96A52] text-white" data-testid={`${testIdPrefix}-report-submit`}>
            {busy ? "Sending..." : "Send report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportContentDialog;
